/**
 * Classroom Management (Multi-Class Support)
 *
 * Functions for managing teacher classrooms and student assignments.
 * Part of the accounts domain (split from accountHelpers.ts).
 *
 * Fase 2 additions (s82): joinCode system, classroom lookup by code,
 * joinClassroomByCode, regenerateJoinCode.
 */

import {
  AccountsState,
  Classroom,
  LearnerAccount,
  isTeacherAccount,
} from '../types'
import { linkTeacherToLearner } from './relationships'
import { generateRandomCode } from '../../utils/randomCode'

// ── ID & Code Generation ──

export function generateClassroomId(): string {
  return `class_${Math.random().toString(36).substring(2, 9)}_${Date.now().toString(36)}`
}

/**
 * Generate a 6-character alphanumeric join code for classrooms.
 * Uses unambiguous characters (no 0/O/1/I/L confusion).
 * Delegates to shared generateRandomCode() (D-SCAN-2 D2-2).
 */
export function generateJoinCode(): string {
  return generateRandomCode(6)
}

/**
 * Generate a unique join code that doesn't collide with existing codes.
 * Scans all classrooms across all teachers.
 */
export function generateUniqueJoinCode(state: AccountsState): string {
  // Collect all existing joinCodes from top-level classroom registry
  const existingCodes = new Set<string>(
    Object.values(state.classrooms).map((c) => c.joinCode),
  )
  let code = generateJoinCode()
  let attempts = 0
  while (existingCodes.has(code) && attempts < 100) {
    code = generateJoinCode()
    attempts++
  }
  return code
}

// ── Classroom Queries ──

export function getTeacherClassrooms(
  state: AccountsState,
  teacherId: string,
): Classroom[] {
  const teacher = state.accountsById[teacherId]
  if (!teacher || teacher.role !== 'teacher') return []

  // Read from top-level classroom registry (canonical source)
  const fromRegistry = Object.values(state.classrooms).filter(
    (c) => c.teacherId === teacherId,
  )
  if (fromRegistry.length > 0) return fromRegistry

  return []
}

/**
 * Find a classroom by its joinCode across all teachers.
 * Returns the classroom and its owning teacherId, or null.
 */
export function findClassroomByJoinCode(
  state: AccountsState,
  joinCode: string,
): { classroom: Classroom; teacherId: string } | null {
  const normalized = joinCode.toUpperCase().trim()
  if (!normalized || normalized.length < 4) return null

  // Search top-level classroom registry (O(classrooms) instead of O(teachers × classrooms))
  const classroom = Object.values(state.classrooms).find(
    (c) => c.joinCode === normalized,
  )
  if (classroom) {
    return { classroom, teacherId: classroom.teacherId }
  }
  return null
}

// ── Classroom Mutations ──

export function addClassroom(
  state: AccountsState,
  teacherId: string,
  classroom: Classroom,
): AccountsState {
  const teacher = state.accountsById[teacherId]
  if (!teacher || teacher.role !== 'teacher') return state

  // Auto-generate joinCode and teacherId if empty/missing
  const enrichedClassroom: Classroom = {
    ...classroom,
    joinCode: classroom.joinCode || generateUniqueJoinCode(state),
    teacherId: classroom.teacherId || teacherId,
  }

  return {
    ...state,
    // Write only to top-level classroom registry (canonical source)
    classrooms: {
      ...state.classrooms,
      [enrichedClassroom.id]: enrichedClassroom,
    },
  }
}

export function updateClassroom(
  state: AccountsState,
  teacherId: string,
  classroomId: string,
  updates: Partial<Omit<Classroom, 'id' | 'createdAt' | 'learnerIds'>>,
): AccountsState {
  const teacher = state.accountsById[teacherId]
  if (!teacher || teacher.role !== 'teacher') return state

  const existing = state.classrooms[classroomId]
  if (!existing || existing.teacherId !== teacherId) return state

  const updatedClassroom = { ...existing, ...updates }

  return {
    ...state,
    classrooms: {
      ...state.classrooms,
      [classroomId]: updatedClassroom,
    },
  }
}

export function removeClassroom(
  state: AccountsState,
  teacherId: string,
  classroomId: string,
): AccountsState {
  const teacher = state.accountsById[teacherId]
  if (!teacher || teacher.role !== 'teacher') return state

  const existing = state.classrooms[classroomId]
  if (!existing || existing.teacherId !== teacherId) return state

  const { [classroomId]: _removed, ...remainingClassrooms } = state.classrooms

  return {
    ...state,
    classrooms: remainingClassrooms,
  }
}

export function addLearnerToClassroom(
  state: AccountsState,
  teacherId: string,
  classroomId: string,
  learnerId: string,
): AccountsState {
  const teacher = state.accountsById[teacherId]
  if (!teacher || teacher.role !== 'teacher') return state

  // First ensure the teacher-learner relationship exists
  let nextState = state
  const currentLearners = state.relationships.teacherToLearners[teacherId] || []
  if (!currentLearners.includes(learnerId)) {
    nextState = linkTeacherToLearner(state, teacherId, learnerId)
  }

  // Update classroom in top-level registry
  const existing = nextState.classrooms[classroomId]
  if (!existing || existing.learnerIds.includes(learnerId)) {
    // Classroom not found or learner already in it — just return with relationship update
    return nextState
  }

  const updatedClassroom = {
    ...existing,
    learnerIds: [...existing.learnerIds, learnerId],
  }

  // Update learnerToClassrooms reverse index
  const currentL2C = nextState.relationships.learnerToClassrooms
  const learnerClassrooms = currentL2C[learnerId] || []
  const updatedL2C = learnerClassrooms.includes(classroomId)
    ? currentL2C
    : { ...currentL2C, [learnerId]: [...learnerClassrooms, classroomId] }

  return {
    ...nextState,
    classrooms: {
      ...nextState.classrooms,
      [classroomId]: updatedClassroom,
    },
    relationships: {
      ...nextState.relationships,
      learnerToClassrooms: updatedL2C,
    },
  }
}

export function removeLearnerFromClassroom(
  state: AccountsState,
  teacherId: string,
  classroomId: string,
  learnerId: string,
): AccountsState {
  const teacher = state.accountsById[teacherId]
  if (!teacher || teacher.role !== 'teacher') return state

  // Update classroom in top-level registry
  const existing = state.classrooms[classroomId]
  if (!existing) return state

  const updatedClassroom = {
    ...existing,
    learnerIds: existing.learnerIds.filter((id) => id !== learnerId),
  }

  // Update learnerToClassrooms reverse index
  const currentL2C = state.relationships.learnerToClassrooms
  const learnerClassrooms = (currentL2C[learnerId] || []).filter(
    (id) => id !== classroomId,
  )
  const updatedL2C = {
    ...currentL2C,
    [learnerId]: learnerClassrooms,
  }

  return {
    ...state,
    classrooms: {
      ...state.classrooms,
      [classroomId]: updatedClassroom,
    },
    relationships: {
      ...state.relationships,
      learnerToClassrooms: updatedL2C,
    },
  }
}

export function getClassroomStudents(
  state: AccountsState,
  teacherId: string,
  classroomId: string,
): LearnerAccount[] {
  const classrooms = getTeacherClassrooms(state, teacherId)
  const classroom = classrooms.find((c) => c.id === classroomId)
  if (!classroom) return []

  return classroom.learnerIds
    .map((id) => state.accountsById[id])
    .filter((a): a is LearnerAccount => a?.role === 'learner')
}

/**
 * Join a classroom by joinCode. Adds the learner to the classroom
 * and creates the teacher-learner relationship if needed.
 * Returns the updated state and result info, or null if code not found.
 */
export function joinClassroomByCode(
  state: AccountsState,
  learnerId: string,
  joinCode: string,
): { state: AccountsState; classroomId: string; teacherId: string } | null {
  const found = findClassroomByJoinCode(state, joinCode)
  if (!found) return null

  const { classroom, teacherId } = found

  // Don't add if already in this classroom
  if (classroom.learnerIds.includes(learnerId)) {
    return { state, classroomId: classroom.id, teacherId }
  }

  // Use existing addLearnerToClassroom which also handles teacher-learner linking
  const nextState = addLearnerToClassroom(
    state,
    teacherId,
    classroom.id,
    learnerId,
  )

  // Auto-sync learner grade from classroom groupLevel
  const learner = nextState.accountsById[learnerId]
  if (learner && learner.role === 'learner' && !learner.grade) {
    const updatedState: AccountsState = {
      ...nextState,
      accountsById: {
        ...nextState.accountsById,
        [learnerId]: {
          ...learner,
          grade: classroom.groupLevel,
        },
      },
    }
    return { state: updatedState, classroomId: classroom.id, teacherId }
  }

  return { state: nextState, classroomId: classroom.id, teacherId }
}

/**
 * Regenerate the joinCode for a classroom.
 * Returns the updated state and the new code.
 */
export function regenerateJoinCode(
  state: AccountsState,
  teacherId: string,
  classroomId: string,
): { state: AccountsState; newCode: string } {
  const newCode = generateUniqueJoinCode(state)
  const updatedState = updateClassroom(state, teacherId, classroomId, {
    joinCode: newCode,
  })
  return { state: updatedState, newCode }
}

// ── Validation & Guardrails ──────────────────────────────────

export interface ClassroomValidationWarning {
  type:
    | 'duplicate_name'
    | 'grade_mismatch'
    | 'already_in_other_classroom'
    | 'already_linked_to_other_teacher'
  message: string
  details: {
    classroomName?: string
    classroomId?: string
    teacherName?: string
    teacherId?: string
    learnerGrade?: number
    classroomGroupLevel?: number
  }
}

/**
 * Check if a classroom name already exists (across ALL teachers).
 * Returns matching classrooms (excluding the one being edited, if excludeId provided).
 */
export function findDuplicateClassroomName(
  state: AccountsState,
  name: string,
  excludeId?: string,
): Array<{ classroom: Classroom; teacherName: string }> {
  const normalized = name.trim().toLowerCase()
  if (!normalized) return []

  const duplicates: Array<{ classroom: Classroom; teacherName: string }> = []
  for (const classroom of Object.values(state.classrooms)) {
    if (excludeId && classroom.id === excludeId) continue
    if (classroom.name.trim().toLowerCase() === normalized) {
      const teacher = state.accountsById[classroom.teacherId]
      duplicates.push({
        classroom,
        teacherName: teacher?.displayName || 'Onbekend',
      })
    }
  }
  return duplicates
}

/**
 * Get all classrooms a learner is currently enrolled in (across all teachers).
 * Returns classroom + teacher info for each.
 */
export function getLearnerExistingClassrooms(
  state: AccountsState,
  learnerId: string,
): Array<{ classroom: Classroom; teacherName: string }> {
  const results: Array<{ classroom: Classroom; teacherName: string }> = []
  for (const classroom of Object.values(state.classrooms)) {
    if (classroom.learnerIds.includes(learnerId)) {
      const teacher = state.accountsById[classroom.teacherId]
      results.push({
        classroom,
        teacherName: teacher?.displayName || 'Onbekend',
      })
    }
  }
  return results
}

/**
 * Validate adding a learner to a classroom. Returns warnings (not errors —
 * the action can still proceed, but the user should be informed).
 */
export function validateAddLearnerToClassroom(
  state: AccountsState,
  teacherId: string,
  classroomId: string,
  learnerId: string,
): ClassroomValidationWarning[] {
  const warnings: ClassroomValidationWarning[] = []
  const classroom = state.classrooms[classroomId]
  const learner = state.accountsById[learnerId]
  if (!classroom || !learner || learner.role !== 'learner') return warnings

  // 1. Grade mismatch check
  if (learner.grade && learner.grade !== classroom.groupLevel) {
    warnings.push({
      type: 'grade_mismatch',
      message: `${learner.displayName} zit in groep ${learner.grade}, maar deze klas is groep ${classroom.groupLevel}.`,
      details: {
        learnerGrade: learner.grade,
        classroomGroupLevel: classroom.groupLevel,
      },
    })
  }

  // 2. Already in another classroom (different teacher)
  const existingClassrooms = getLearnerExistingClassrooms(state, learnerId)
  for (const existing of existingClassrooms) {
    if (existing.classroom.id === classroomId) continue // Same classroom — skip
    if (existing.classroom.teacherId === teacherId) continue // Same teacher, different class — OK
    warnings.push({
      type: 'already_in_other_classroom',
      message: `${learner.displayName} zit al in "${existing.classroom.name}" van ${existing.teacherName}. Wil je dat deze leerling bij beide leraren zichtbaar is?`,
      details: {
        classroomName: existing.classroom.name,
        classroomId: existing.classroom.id,
        teacherName: existing.teacherName,
        teacherId: existing.classroom.teacherId,
      },
    })
  }

  // 3. Already linked to another teacher (but not in a classroom)
  for (const [otherTeacherId, learnerIds] of Object.entries(
    state.relationships.teacherToLearners,
  )) {
    if (otherTeacherId === teacherId) continue
    if (learnerIds.includes(learnerId)) {
      // Check if we already warned about this teacher via classroom
      const alreadyWarned = warnings.some(
        (w) =>
          w.type === 'already_in_other_classroom' &&
          w.details.teacherId === otherTeacherId,
      )
      if (!alreadyWarned) {
        const otherTeacher = state.accountsById[otherTeacherId]
        warnings.push({
          type: 'already_linked_to_other_teacher',
          message: `${learner.displayName} is al gekoppeld aan ${otherTeacher?.displayName || 'een andere leraar'}.`,
          details: {
            teacherName: otherTeacher?.displayName || 'Onbekend',
            teacherId: otherTeacherId,
          },
        })
      }
    }
  }

  return warnings
}

/**
 * Validate creating a new classroom. Returns warnings about duplicate names.
 */
export function validateNewClassroom(
  state: AccountsState,
  name: string,
  teacherId: string,
): ClassroomValidationWarning[] {
  const warnings: ClassroomValidationWarning[] = []
  const duplicates = findDuplicateClassroomName(state, name)

  for (const dup of duplicates) {
    const isSameTeacher = dup.classroom.teacherId === teacherId
    warnings.push({
      type: 'duplicate_name',
      message: isSameTeacher
        ? `Je hebt al een klas met de naam "${dup.classroom.name}".`
        : `"${dup.classroom.name}" bestaat al bij ${dup.teacherName}. Wil je toch een klas met dezelfde naam aanmaken?`,
      details: {
        classroomName: dup.classroom.name,
        classroomId: dup.classroom.id,
        teacherName: dup.teacherName,
        teacherId: dup.classroom.teacherId,
      },
    })
  }

  return warnings
}

// ── School-Based Classroom Discovery ──

export interface ClassroomWithTeacher {
  classroom: Classroom
  teacherName: string
  teacherId: string
  schoolName: string
}

/**
 * Get ALL classrooms with teacher info. Useful as fallback when no same-school classrooms exist.
 */
export function getAllClassroomsWithTeachers(
  state: AccountsState,
): ClassroomWithTeacher[] {
  const results: ClassroomWithTeacher[] = []
  for (const classroom of Object.values(state.classrooms)) {
    const teacher = state.accountsById[classroom.teacherId]
    const school =
      teacher && isTeacherAccount(teacher) ? teacher.schoolName || '' : ''
    results.push({
      classroom,
      teacherName: teacher?.displayName || 'Onbekend',
      teacherId: classroom.teacherId,
      schoolName: school,
    })
  }
  return results.sort((a, b) =>
    a.classroom.name.localeCompare(b.classroom.name),
  )
}

/**
 * Get all classrooms belonging to a specific school (by schoolName on teachers).
 * Returns classrooms with teacher info, useful for "search existing classrooms" UIs.
 */
export function getSchoolClassrooms(
  state: AccountsState,
  schoolName: string,
): Array<{ classroom: Classroom; teacherName: string; teacherId: string }> {
  if (!schoolName.trim()) return []
  const normalized = schoolName.trim().toLowerCase()

  // Find all teacher IDs at this school
  const schoolTeacherIds = new Set<string>()
  for (const account of Object.values(state.accountsById)) {
    if (
      isTeacherAccount(account) &&
      account.schoolName?.trim().toLowerCase() === normalized
    ) {
      schoolTeacherIds.add(account.id)
    }
  }

  // Find all classrooms owned by those teachers
  const results: Array<{
    classroom: Classroom
    teacherName: string
    teacherId: string
  }> = []
  for (const classroom of Object.values(state.classrooms)) {
    if (schoolTeacherIds.has(classroom.teacherId)) {
      const teacher = state.accountsById[classroom.teacherId]
      results.push({
        classroom,
        teacherName: teacher?.displayName || 'Onbekend',
        teacherId: classroom.teacherId,
      })
    }
  }
  return results.sort((a, b) =>
    a.classroom.name.localeCompare(b.classroom.name),
  )
}

/**
 * Transfer classroom ownership to a new teacher.
 * The old teacher loses ownership; the new teacher becomes the sole owner.
 */
export function transferClassroomOwnership(
  state: AccountsState,
  classroomId: string,
  newTeacherId: string,
): AccountsState {
  const classroom = state.classrooms[classroomId]
  if (!classroom) return state

  // Update classroom teacherId
  let newState: AccountsState = {
    ...state,
    classrooms: {
      ...state.classrooms,
      [classroomId]: {
        ...classroom,
        teacherId: newTeacherId,
      },
    },
  }

  // Link new teacher to all learners in the classroom
  for (const learnerId of classroom.learnerIds) {
    newState = linkTeacherToLearner(newState, newTeacherId, learnerId)
  }

  return newState
}

/**
 * Join an existing classroom as co-teacher (mee-kijken).
 * Links the teacher to all learners in the classroom without changing ownership.
 * The original teacher remains the owner.
 */
export function joinClassroomAsCoTeacher(
  state: AccountsState,
  teacherId: string,
  classroomId: string,
): AccountsState {
  const classroom = state.classrooms[classroomId]
  if (!classroom) return state

  // Link teacher to all learners in the classroom
  let newState = state
  for (const learnerId of classroom.learnerIds) {
    newState = linkTeacherToLearner(newState, teacherId, learnerId)
  }

  return newState
}
